import { Component, Input, Output, EventEmitter, ElementRef, ChangeDetectorRef, OnDestroy, HostListener } from '@angular/core';
import { trigger, transition, style, animate, state, AnimationEvent } from '@angular/animations';

@Component({
	selector: 'ui-sidebar',
	template:
		`<div class="ui-sidebar-container" *ngIf="visible">
			<div class="ui-sidebar-backdrop" [@fadeInOut]="animState" (click)="close()"></div>
			<div class="ui-sidebar-panel"
				[@slideInOut]="animState === 'open' ? position : 'closed-' + position"
				(@slideInOut.done)="onAnimationDone($event)"
				[style.width]="width"
				[style.left]="position === 'left' ? '0' : null"
				[style.right]="position === 'right' ? '0' : null">
				<ng-content></ng-content>
			</div>
		</div>`,
	styles: [`
		.ui-sidebar-container {
			position: fixed;
			z-index: 10;
			overflow: hidden;
		}
		.ui-sidebar-backdrop {
			position: absolute;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			background: rgba(0, 0, 0, 0.3);
		}
		.ui-sidebar-panel {
			position: absolute;
			top: 0;
			bottom: 0;
			background: #fff;
			overflow-y: auto;
			box-shadow: 0 0 15px rgba(0, 0, 0, 0.2);
		}
	`],
	animations: [
		trigger('fadeInOut', [
			state('open', style({ opacity: 1 })),
			state('closed', style({ opacity: 0 })),
			transition('closed => open', [
				style({ opacity: 0 }),
				animate('200ms ease-in-out')
			]),
			transition('open => closed', [
				animate('200ms ease-in-out')
			])
		]),
		trigger('slideInOut', [
			state('left', style({ transform: 'translateX(0)' })),
			state('right', style({ transform: 'translateX(0)' })),
			state('closed-left', style({ transform: 'translateX(-100%)' })),
			state('closed-right', style({ transform: 'translateX(100%)' })),
			transition('* => left', [
				style({ transform: 'translateX(-100%)' }),
				animate('300ms ease-in-out')
			]),
			transition('left => closed-left', [
				animate('300ms ease-in-out')
			]),
			transition('* => right', [
				style({ transform: 'translateX(100%)' }),
				animate('300ms ease-in-out')
			]),
			transition('right => closed-right', [
				animate('300ms ease-in-out')
			])
		])
	]
})
export class UISidebarComponent implements OnDestroy {

	@Input() position: 'left' | 'right' = 'left';
	@Input() width = '20rem';
	// When true the drawer covers the whole viewport (backdrop over the nav too)
	// instead of being confined to its parent element's bounds.
	@Input() fullscreen = false;

	@Output() onOpen = new EventEmitter<void>();
	@Output() onClose = new EventEmitter<void>();

	isOpen = false;
	visible = false;
	animState = 'closed';

	constructor(private el: ElementRef, private cdr: ChangeDetectorRef) {}

	@HostListener('window:resize')
	onResize() {
		if (this.isOpen) {
			this.positionContainer();
		}
	}

	ngOnDestroy() {}

	open() {
		if (this.isOpen) return;
		this.isOpen = true;
		this.visible = true;
		this.animState = 'open';
		this.cdr.detectChanges();
		this.positionContainer();
		this.onOpen.emit();
	}

	close() {
		if (!this.isOpen) return;
		this.isOpen = false;
		this.animState = 'closed';
		this.onClose.emit();
	}

	reset() {
		this.isOpen = false;
		this.visible = false;
		this.animState = 'closed';
	}

	onAnimationDone(event: AnimationEvent) {
		if (event.toState === 'closed-left' || event.toState === 'closed-right') {
			this.visible = false;
		}
	}

	private positionContainer() {
		const container = this.el.nativeElement.querySelector('.ui-sidebar-container');
		if (!container) return;

		// Full-viewport mode: cover everything (incl. the app nav) and sit above it.
		if (this.fullscreen) {
			container.style.top = '0';
			container.style.left = '0';
			container.style.width = '100vw';
			container.style.height = '100vh';
			container.style.zIndex = '1050';
			// Let the projected content own its scrolling (fixed header + scroll body)
			// instead of the panel scrolling as a whole — avoids a double scrollbar.
			const panel = this.el.nativeElement.querySelector('.ui-sidebar-panel');
			if (panel) panel.style.overflowY = 'hidden';
			return;
		}

		const parent = this.el.nativeElement.parentElement;
		if (parent) {
			const rect = parent.getBoundingClientRect();
			container.style.top = rect.top + 'px';
			container.style.left = rect.left + 'px';
			container.style.width = rect.width + 'px';
			container.style.height = rect.height + 'px';
		}
	}

}
